import React, { FunctionComponent, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ILeadPreferences, EMPTY_LEAD_PREFERENCES, COMPANY_SIZE_OPTIONS } from '../../../../interfaces/models/campaign';
import { prospectIndustryOptions } from '../../../../assets/utils/prospectIndustryOptions';
import { countryOptions } from '../../../../assets/utils/countryOptions';
import { useProspectsHandlers } from '../../../../services/actions/prospects/useProspectsHandlers';
import Column from '../../../../components/Column';
import Text from '../../../../components/Text';
import SvgFragment from '../../../../fragments/SvgFragment';
import TooltipWrapper from '../../../../components/TooltipWrapper';

interface IOption { value: string; label: string }

const INDUSTRY_OPTIONS = prospectIndustryOptions.filter(o => o.value !== null) as IOption[];

const SIZE_OPTIONS: IOption[] = COMPANY_SIZE_OPTIONS.map(s => ({ value: s, label: s }));

const JOB_TITLE_OPTIONS: IOption[] = [
  'CEO', 'CTO', 'CFO', 'COO', 'CMO',
  'VP Sales', 'VP Marketing', 'VP Engineering', 'VP Product',
  'Director of Sales', 'Director of Marketing', 'Head of Growth',
  'Product Manager', 'Engineering Manager', 'Sales Manager',
  'Founder', 'Co-Founder', 'Owner', 'Partner', 'Principal',
].map(t => ({ value: t, label: t }));

interface Props {
  value: ILeadPreferences;
  onChange: (prefs: ILeadPreferences) => void;
  /** When false, filters are optional (e.g. manual lead import path). Default true. */
  required?: boolean;
  /** Import ceiling control — rendered inside the panel when both are provided. */
  leadLimit?: number;
  leadLimitOptions?: number[];
  onLeadLimitChange?: (value: string) => void;
  leadLimitTooltip?: string;
  /** Defaults to resetting every filter through `onChange`. */
  onClearFilters?: () => void;
}

/**
 * One control shape for every filter: a chip box that doubles as the dropdown trigger.
 * Values can come from a predefined list, from an async autocomplete source, or be typed
 * freely and committed with Enter — each field opts into whichever of the three it needs.
 */
interface MultiSelectFieldProps {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** Predefined values. When present the field renders a chevron and a picker list. */
  options?: IOption[];
  /** Async suggestion source; results are plain strings (value === label). */
  fetchOptions?: (query: string) => Promise<string[]>;
  /** Allow committing arbitrary text with Enter. Off for code-backed fields (country, industry). */
  allowFreeText?: boolean;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label, placeholder, values, onChange, options, fetchOptions, allowFreeText = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only a predefined list earns a chevron: an autocomplete-only field has nothing to show
  // until something is typed, so a chevron there would open onto an empty panel.
  const hasStaticOptions = !!options?.length;
  const hasSuggestions = hasStaticOptions || !!fetchOptions;

  const labelMap = useMemo(
    () => new Map((options ?? []).map(o => [o.value, o.label])),
    [options]
  );
  const labelFor = (v: string) => labelMap.get(v) ?? v;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setRemote([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [close]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const runFetch = useCallback((q: string) => {
    if (!fetchOptions) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setRemote([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        setRemote(await fetchOptions(q));
      } catch {
        setRemote([]);
      }
      setLoading(false);
    }, 280);
  }, [fetchOptions]);

  // Predefined values first, then whatever the autocomplete added on top of them. The
  // location API returns one row per place, so several same-named cities collapse into one.
  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const statics = (options ?? []).filter(o => !q || o.label.toLowerCase().includes(q));
    const seen = new Set(statics.map(o => o.value));
    const remotes: IOption[] = [];
    remote.forEach(r => {
      if (seen.has(r)) return;
      seen.add(r);
      remotes.push({ value: r, label: r });
    });
    return [...statics, ...remotes].slice(0, 80);
  }, [options, remote, query]);

  const add = (v: string) => {
    const t = v.trim();
    if (!t) return;
    if (!values.includes(t)) onChange([...values, t]);
    setQuery('');
    setRemote([]);
    inputRef.current?.focus();
  };

  const remove = (v: string) => onChange(values.filter(x => x !== v));

  const commit = () => {
    const t = query.trim();
    if (!t) return;
    // A typed value that matches a known option is stored as that option's value, so
    // "Germany" ends up as "DE" rather than as free text the API cannot match.
    const match = visibleOptions.find(o => o.label.toLowerCase() === t.toLowerCase());
    if (match) { add(match.value); return; }
    if (allowFreeText) { add(t); return; }
    if (visibleOptions.length === 1) add(visibleOptions[0].value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Backspace' && !query && values.length > 0) { remove(values[values.length - 1]); }
    else if (e.key === 'Escape') { close(); inputRef.current?.blur(); }
  };

  const canAddTyped =
    allowFreeText &&
    !!query.trim() &&
    !values.some(v => labelFor(v).toLowerCase() === query.trim().toLowerCase()) &&
    !visibleOptions.some(o => o.label.toLowerCase() === query.trim().toLowerCase());

  // Free-text fields say so in the placeholder — otherwise the control reads as a picker
  // and nobody discovers that a value can simply be typed.
  const inputPlaceholder = (() => {
    if (open && allowFreeText) return 'Type and press Enter to add';
    // Space next to the chips is tight, so the long hint is only shown on the empty field.
    if (values.length > 0) return 'Add more…';
    if (allowFreeText && hasSuggestions) return `${placeholder} or type your own`;
    return placeholder;
  })();

  // Nothing to show is nothing to open: a panel holding only a hint is noise.
  const showOptionList = loading || visibleOptions.length > 0;
  const showPanel = open && (values.length > 0 || canAddTyped || showOptionList);

  return (
    <Field ref={wrapRef} $open={open}>
      <FieldLabel>
        {label}
        {values.length > 0 && <CountBadge>{values.length}</CountBadge>}
      </FieldLabel>

      <Control
        $open={open}
        $hasCaret={hasStaticOptions}
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {values.map(v => (
          <Chip key={v} onClick={e => e.stopPropagation()}>
            <ChipText>{labelFor(v)}</ChipText>
            <ChipRemove
              type="button"
              aria-label={`Remove ${labelFor(v)}`}
              onClick={() => remove(v)}
            >
              ×
            </ChipRemove>
          </Chip>
        ))}
        <ControlInput
          ref={inputRef}
          type="text"
          value={query}
          placeholder={inputPlaceholder}
          onChange={e => { setQuery(e.target.value); setOpen(true); runFetch(e.target.value); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
        />
        {hasStaticOptions && <Caret $open={open} aria-hidden><SvgFragment type="dropdownArrow" /></Caret>}
      </Control>

      {showPanel && (
        <Panel>
          {/* The picked values are already chips in the control, so the panel only carries
              the count and a way to drop them all at once. */}
          {values.length > 0 && (
            <PanelSelected>
              <span>{values.length} selected</span>
              <PanelClear type="button" onMouseDown={e => { e.preventDefault(); onChange([]); }}>
                Clear
              </PanelClear>
            </PanelSelected>
          )}

          {canAddTyped && (
            <AddTypedRow onMouseDown={e => { e.preventDefault(); commit(); }}>
              <AddTypedIcon><SvgFragment type="plusLine" /></AddTypedIcon>
              Add “{query.trim()}”
            </AddTypedRow>
          )}

          {showOptionList && (
            <OptionList>
              {loading && <PanelHint>Searching…</PanelHint>}
              {visibleOptions.map(o => {
                const selected = values.includes(o.value);
                return (
                  <OptionRow
                    key={o.value}
                    $selected={selected}
                    onMouseDown={e => { e.preventDefault(); if (selected) remove(o.value); else add(o.value); }}
                  >
                    <OptionCheck $selected={selected}>
                      {selected && <SvgFragment type="checkmark" />}
                    </OptionCheck>
                    {o.label}
                  </OptionRow>
                );
              })}
            </OptionList>
          )}
        </Panel>
      )}
    </Field>
  );
};

const CampaignLeadPreferences: FunctionComponent<Props> = ({
  value, onChange, required = true,
  leadLimit, leadLimitOptions, onLeadLimitChange, leadLimitTooltip, onClearFilters,
}) => {
  const {
    autocompleteJobTitlesHandler,
    autocompleteLocationsHandler,
    autocompleteCompaniesHandler,
  } = useProspectsHandlers();
  const [expanded, setExpanded] = useState(true);

  const update = (patch: Partial<ILeadPreferences>) => onChange({ ...value, ...patch });

  const fetchJobTitles = useCallback(async (q: string) => {
    const res = await autocompleteJobTitlesHandler({ name: q });
    return ((res?.job_titles ?? []) as any[]).map((t: any) =>
      typeof t === 'string' ? t : (t.name ?? String(t))
    );
  }, []);

  const fetchCities = useCallback(async (q: string) => {
    const res = await autocompleteLocationsHandler({ name: q });
    return ((res?.locations ?? []) as any[])
      .filter((l: any) => l.type === 'city')
      .map((l: any) => l.name as string);
  }, []);

  // Same source the LeadSearch page uses for its company field.
  const fetchCompanies = useCallback(async (q: string) => {
    const res = await autocompleteCompaniesHandler({ name: q });
    return ((res?.companies ?? []) as any[]).map((c: any) =>
      typeof c === 'string' ? c : (c.name ?? String(c))
    );
  }, []);

  const fetchStates = useCallback(async (q: string) => {
    const res = await autocompleteLocationsHandler({ name: q });
    return ((res?.locations ?? []) as any[])
      .filter((l: any) => l.type === 'state')
      .map((l: any) => l.name as string);
  }, []);

  const totalFilters =
    value.jobTitle.length + value.countryCode.length + value.city.length +
    value.state.length + (value.school?.length ?? 0) + value.companyName.length +
    value.companyIndustry.length + value.companySize.length;

  const clearAll = () => (onClearFilters ? onClearFilters() : onChange(EMPTY_LEAD_PREFERENCES));

  const showLeadLimit = leadLimit != null && !!onLeadLimitChange && !!leadLimitOptions?.length;

  const limitControl = showLeadLimit ? (
    <LeadLimitBox>
      <LeadLimitLabel>Max leads to import</LeadLimitLabel>
      <LeadLimitSelectWrap>
        <LeadLimitSelect
          value={leadLimit}
          onChange={e => onLeadLimitChange!(e.target.value)}
          aria-label="Max leads to import"
        >
          {leadLimitOptions!.map(n => <option key={n} value={n}>{n}</option>)}
        </LeadLimitSelect>
        <LeadLimitChevron aria-hidden><SvgFragment type="arrowDown" /></LeadLimitChevron>
      </LeadLimitSelectWrap>
    </LeadLimitBox>
  ) : null;

  return (
    <Panel_>
      <PanelHead>
        <HeadLeft type="button" onClick={() => setExpanded(v => !v)}>
          <HeadCaret $open={expanded} aria-hidden><SvgFragment type="dropdownArrow" /></HeadCaret>
          <Text $bold style={{ fontSize: '14px' }}>Lead targeting preferences</Text>
          {totalFilters > 0
            ? <FilterBadge>{totalFilters} filter{totalFilters !== 1 ? 's' : ''}</FilterBadge>
            : required
              ? <RequiredBadge>Required</RequiredBadge>
              : <OptionalBadge>Optional</OptionalBadge>}
        </HeadLeft>
        <ClearFiltersBtn type="button" onClick={clearAll} disabled={totalFilters === 0}>
          Clear filters
        </ClearFiltersBtn>
      </PanelHead>

      {expanded && (
        <PanelBody>
          {limitControl && (
            leadLimitTooltip
              ? <TooltipWrapper tooltipText={leadLimitTooltip} placement="top">{limitControl}</TooltipWrapper>
              : limitControl
          )}

          <GroupLabel>Lead filters</GroupLabel>
          <FieldGrid>
            <MultiSelectField
              label="Job titles"
              placeholder="Add job titles"
              values={value.jobTitle}
              onChange={v => update({ jobTitle: v })}
              options={JOB_TITLE_OPTIONS}
              fetchOptions={fetchJobTitles}
              allowFreeText
            />
            <MultiSelectField
              label="Country"
              placeholder="Add countries"
              values={value.countryCode}
              onChange={v => update({ countryCode: v })}
              options={countryOptions}
            />
            <MultiSelectField
              label="City"
              placeholder="Add cities"
              values={value.city}
              onChange={v => update({ city: v })}
              fetchOptions={fetchCities}
              allowFreeText
            />
            <MultiSelectField
              label="State / Region"
              placeholder="Add states or regions"
              values={value.state}
              onChange={v => update({ state: v })}
              fetchOptions={fetchStates}
              allowFreeText
            />
            <MultiSelectField
              label="School"
              placeholder="Add a school"
              values={value.school ?? []}
              onChange={v => update({ school: v })}
              allowFreeText
            />
          </FieldGrid>

          <GroupLabel>Company filters</GroupLabel>
          <FieldGrid>
            <MultiSelectField
              label="Industry"
              placeholder="Add industries"
              values={value.companyIndustry}
              onChange={v => update({ companyIndustry: v })}
              options={INDUSTRY_OPTIONS}
            />
            <MultiSelectField
              label="Company size (employees)"
              placeholder="Add company sizes"
              values={value.companySize}
              onChange={v => update({ companySize: v })}
              options={SIZE_OPTIONS}
            />
            <MultiSelectField
              label="Company name"
              placeholder="Add company names"
              values={value.companyName}
              onChange={v => update({ companyName: v })}
              fetchOptions={fetchCompanies}
              allowFreeText
            />
          </FieldGrid>
        </PanelBody>
      )}
    </Panel_>
  );
};

// ---- Styled Components ----

const Panel_ = styled(Column)`
  background: ${styles.colors.white};
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  gap: 0;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
`;

const HeadLeft = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const HeadCaret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
  .svg  { width: 13px; height: 13px; display: block; }
  .path { fill: ${styles.colors.black400}; }
`;

const PanelBody = styled(Column)`
  gap: 14px;
  padding: 0 18px 18px;
`;

const FilterBadge = styled.span`
  padding: 2px 9px;
  background: ${styles.colors.primary600};
  color: ${styles.colors.white};
  border-radius: 20px;
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 18px;
  padding: 0 9px 0 7px;
  border-radius: 20px;
  background: #fbf7ef;
  color: #9a6f22;
  border: 1px solid #ece0c9;
  font-family: NunitoSans;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  box-sizing: border-box;
  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #e0a93e;
    flex-shrink: 0;
  }
`;

const OptionalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 20px;
  background: ${styles.colors.black100};
  color: ${styles.colors.black400};
  border: 1px solid #dde3ee;
  font-family: NunitoSans;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
`;

const ClearFiltersBtn = styled.button`
  height: 32px;
  padding: 0 14px;
  border: 1px solid #dde3ee;
  border-radius: 8px;
  background: ${styles.colors.white};
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.black500};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: ${styles.colors.primary600};
    color: ${styles.colors.primary600};
  }
  &:disabled { opacity: 0.5; cursor: default; }
`;

// Import ceiling sits in its own boxed control, apart from the filter grid below it.
const LeadLimitBox = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  width: 210px;
  padding: 12px;
  border: 1px solid #e8ecf3;
  border-radius: 10px;
  box-sizing: border-box;
`;

const LeadLimitLabel = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.black400};
  white-space: nowrap;
`;

const LeadLimitSelectWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const LeadLimitSelect = styled.select`
  appearance: none;
  width: 100%;
  height: 38px;
  padding: 0 30px 0 12px;
  border: 1px solid #dde3ee;
  border-radius: 8px;
  background: ${styles.colors.white};
  cursor: pointer;
  outline: none;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 600;
  color: ${styles.colors.black500};
  &:focus { border-color: ${styles.colors.primary600}; }
`;

const LeadLimitChevron = styled.span`
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
  .svg  { width: 14px; height: 14px; display: block; }
  .path { fill: ${styles.colors.black400}; }
`;

const GroupLabel = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${styles.colors.black300};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 18px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

// The open field has to sit above the rows beneath it, or its panel is clipped by them.
// `align-self: start` keeps the field its own height instead of stretching to the tallest
// one in the row — otherwise the panel, anchored to the field's bottom, floats far below
// the control it belongs to.
const Field = styled.div<{ $open: boolean }>`
  position: relative;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  z-index: ${({ $open }) => ($open ? 140 : 1)};
`;

const FieldLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.black500};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: 20px;
  background: ${styles.colors.primary100};
  color: ${styles.colors.primary600};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const Control = styled.div<{ $open: boolean; $hasCaret: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 38px;
  padding: ${({ $hasCaret }) => ($hasCaret ? '4px 30px 4px 8px' : '4px 10px 4px 8px')};
  border: 1px solid ${({ $open }) => ($open ? styles.colors.primary600 : '#dde3ee')};
  border-radius: 8px;
  background: ${styles.colors.white};
  cursor: text;
  position: relative;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${({ $open }) => ($open ? '0 0 0 3px rgba(89,100,255,0.08)' : 'none')};
  &:hover { border-color: ${({ $open }) => ($open ? styles.colors.primary600 : '#c9d2e4')}; }
`;

const ControlInput = styled.input`
  flex: 1 1 60px;
  min-width: 60px;
  height: 28px;
  border: none;
  outline: none;
  background: transparent;
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black600};
  &::placeholder { color: ${styles.colors.black300}; }
`;

const Caret = styled.span<{ $open: boolean }>`
  position: absolute;
  right: 10px;
  top: 9px;
  display: inline-flex;
  pointer-events: none;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
  .svg  { width: 13px; height: 13px; display: block; }
  .path { fill: ${styles.colors.black300}; }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 6px 3px 9px;
  background: ${styles.colors.primary100};
  border: 1px solid ${styles.colors.primary200};
  border-radius: 20px;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.primary600};
  line-height: 1.35;
`;

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 190px;
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: ${styles.colors.primary300};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  &:hover { color: ${styles.colors.red600}; }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 150;
  background: ${styles.colors.white};
  border: 1px solid #dde3ee;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(20,30,80,0.14);
  overflow: hidden;
`;

const PanelSelected = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  border-bottom: 1px solid #eef2f7;
  background: #fafbff;
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${styles.colors.black400};
`;

const PanelClear = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  color: ${styles.colors.primary600};
  &:hover { text-decoration: underline; }
`;

const AddTypedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 12px;
  border-bottom: 1px solid #eef2f7;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 600;
  color: ${styles.colors.primary600};
  cursor: pointer;
  &:hover { background: ${styles.colors.primary100}; }
`;

const AddTypedIcon = styled.span`
  display: inline-flex;
  .svg  { width: 13px; height: 13px; display: block; }
  .path { fill: ${styles.colors.primary600}; stroke: ${styles.colors.primary600}; }
`;

const OptionList = styled.div`
  max-height: 210px;
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #e8eaed transparent;
`;

const OptionRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  font-family: NunitoSans;
  font-size: 13px;
  cursor: pointer;
  color: ${({ $selected }) => ($selected ? styles.colors.primary600 : styles.colors.black600)};
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};
  background: ${({ $selected }) => ($selected ? styles.colors.primary100 : 'transparent')};
  &:hover { background: ${styles.colors.primary100}; color: ${styles.colors.primary600}; }
`;

const OptionCheck = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid ${({ $selected }) => ($selected ? styles.colors.primary600 : '#dde3ee')};
  background: ${({ $selected }) => ($selected ? styles.colors.primary600 : styles.colors.white)};
  .svg  { width: 10px; height: 10px; display: block; }
  .path { fill: ${styles.colors.white}; stroke: ${styles.colors.white}; }
`;

const PanelHint = styled.div`
  padding: 10px 12px;
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.black300};
`;

export default CampaignLeadPreferences;
